import React, { useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Divider,
  useTheme,
  ListItemIcon,
} from "@mui/material";
import {
  PersonOutline as PersonIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  HelpOutline as HelpIcon,
  NotificationsNone as NotificationsIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { authClient } from "../../lib/auth-client";
import { isNative } from "../../lib/platform";
import { useLogout } from "../../hooks/useLogout";
import { useTouchLayout } from "../../hooks/useTouchLayout";
import HelpModal from "./HelpModal";
import NotificationCountChip from "../Notifications/NotificationCountChip";
import { useNotificationsContext } from "../Notifications/NotificationsProvider";
import { useInboxEnabled } from "../Notifications/hooks/useInboxEnabled";
import { useRecentNotificationCount } from "../Notifications/hooks/useRecentNotificationCount";
import { overlaySurface, floatShadow, panelBorder } from "../Dashboard/overviewPanel";

const UserProfileButton: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const open = Boolean(anchorEl);
  const { mutate: logout } = useLogout();
  const { openInbox } = useNotificationsContext();
  const inboxEnabled = useInboxEnabled();
  const unreadCount = useRecentNotificationCount();
  const isTouch = useTouchLayout();

  // Get current session
  const { data: session } = authClient.useSession();
  const user = session?.user;
  // Admin tooling is web-only — never surfaced inside the native app
  const isAdmin = session?.session?.is_admin === true && !isNative();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSettings = () => {
    handleClose();
    navigate("/auth/settings");
  };

  const handleHelp = () => {
    handleClose();
    setHelpModalOpen(true);
  };

  const handleNotifications = () => {
    handleClose();
    openInbox();
  };

  const handleHelpModalClose = () => {
    setHelpModalOpen(false);
  };

  const handleSignOut = () => {
    handleClose();
    logout();
  };

  const handleAdmin = () => {
    handleClose();
    navigate("/admin");
  };

  // Both breakpoints: MUI's own `sm` rule would otherwise reset minHeight to auto.
  const rowHeight = isTouch ? 44 : 36;
  const itemSx = {
    minHeight: { xs: rowHeight, sm: rowHeight },
    py: 0.5,
    px: 2,
    "&:hover": {
      backgroundColor: theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.05)"
        : "rgba(0, 0, 0, 0.04)",
    },
  };
  const itemIconSx = { minWidth: 32 };
  const dividerSx = { my: 0.5, borderColor: theme.palette.divider };

  return (
    <>
      <IconButton
        onClick={handleClick}
        size="small"
        sx={{
          padding: 0,
          "&:hover": {
            backgroundColor: "transparent",
          },
        }}
      >
        {/* Count of notifications in Recent; MUI renders "99+" above `max`
            and hides the badge at zero. */}
        <Badge
          badgeContent={unreadCount}
          max={99}
          overlap="circular"
          sx={{
            "& .MuiBadge-badge": {
              minWidth: 16,
              height: 16,
              px: 0.5,
              fontSize: "0.625rem",
              fontWeight: 700,
              color: "#ffffff",
              backgroundColor: theme.palette.primary.main,
            },
          }}
        >
          <Box
            sx={{
              padding: "2px",
              borderRadius: "50%",
              border: `1px solid ${theme.palette.primary.main}`,
              display: "inline-flex",
              cursor: "pointer",
              transition: "transform 0.2s ease",
              "&:hover": {
                transform: "scale(1.05)",
              },
            }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                backgroundColor: theme.palette.mode === "dark"
                  ? "#2a2a2a"
                  : "#f8f9fa",
                color: theme.palette.primary.main,
              }}
              src={user?.image || undefined}
            >
              <PersonIcon sx={{ fontSize: "1.125rem" }} />
            </Avatar>
          </Box>
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              minWidth: 220,
              mt: 0.75,
              backgroundColor: overlaySurface(theme.palette.mode === "dark"),
              border: `1px solid ${panelBorder(theme.palette.mode === "dark")}`,
              borderRadius: 1.5,
              boxShadow: floatShadow(theme.palette.mode === "dark"),
              "& .MuiAvatar-root": {
                width: 32,
                height: 32,
              },
            },
          },
          list: { sx: { py: 0.5 } },
        }}
      >
        {/* User Info Section */}
        <Box sx={{ px: 2, pt: 0.5, pb: 0.5 }}>
          <Typography
            sx={{
              fontSize: "0.8125rem",
              fontWeight: 600,
              color: theme.palette.text.primary,
              lineHeight: 1.2,
            }}
          >
            {user?.name || "User"}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 400,
              color: theme.palette.text.secondary,
              lineHeight: 1.3,
              mt: 0.125,
            }}
          >
            {user?.email || ""}
          </Typography>
        </Box>

        <Divider sx={dividerSx} />

        {/* Notifications — opens the inbox */}
        {inboxEnabled && (
          <MenuItem
            onClick={handleNotifications}
            sx={itemSx}
          >
            <ListItemIcon sx={itemIconSx}>
              <NotificationsIcon
                fontSize="small"
                sx={{ color: theme.palette.text.secondary }}
              />
            </ListItemIcon>
            <Typography
              sx={{
                flex: 1,
                fontSize: "0.875rem",
                color: theme.palette.text.primary,
                fontWeight: 500,
              }}
            >
              Notifications
            </Typography>
            {unreadCount > 0 && (
              <NotificationCountChip count={unreadCount} />
            )}
          </MenuItem>
        )}

        {/* Help */}
        <MenuItem
          onClick={handleHelp}
          sx={itemSx}
        >
          <ListItemIcon sx={itemIconSx}>
            <HelpIcon
              fontSize="small"
              sx={{ color: theme.palette.text.secondary }}
            />
          </ListItemIcon>
          <Typography
            sx={{
              fontSize: "0.875rem",
              color: theme.palette.text.primary,
              fontWeight: 500,
            }}
          >
            Help
          </Typography>
        </MenuItem>

        {/* Account Settings */}
        <MenuItem
          onClick={handleSettings}
          sx={itemSx}
        >
          <ListItemIcon sx={itemIconSx}>
            <SettingsIcon
              fontSize="small"
              sx={{ color: theme.palette.text.secondary }}
            />
          </ListItemIcon>
          <Typography
            sx={{
              fontSize: "0.875rem",
              color: theme.palette.text.primary,
              fontWeight: 500,
            }}
          >
            Account Settings
          </Typography>
        </MenuItem>

        {isAdmin && (
          <>
            <Divider sx={dividerSx} />
            <MenuItem
              onClick={handleAdmin}
              sx={itemSx}
            >
              <ListItemIcon sx={itemIconSx}>
                <AdminPanelSettingsIcon
                  fontSize="small"
                  sx={{ color: theme.palette.text.secondary }}
                />
              </ListItemIcon>
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  color: theme.palette.text.primary,
                  fontWeight: 500,
                }}
              >
                Admin Panel
              </Typography>
            </MenuItem>
          </>
        )}

        <Divider sx={dividerSx} />

        {/* Sign Out */}
        <MenuItem
          onClick={handleSignOut}
          sx={itemSx}
        >
          <ListItemIcon sx={itemIconSx}>
            <LogoutIcon
              fontSize="small"
              sx={{ color: theme.palette.error.main }}
            />
          </ListItemIcon>
          <Typography
            sx={{
              fontSize: "0.875rem",
              color: theme.palette.error.main,
              fontWeight: 500,
            }}
          >
            Sign Out
          </Typography>
        </MenuItem>
      </Menu>

      {/* Help Modal */}
      <HelpModal open={helpModalOpen} onClose={handleHelpModalClose} />
    </>
  );
};

export default UserProfileButton;
